// launch-redesign T34 (E17, E18, E28): per-branch rows for one criterion. Renders the PROXY's unit answer
// (rollup `criteria[].redesign.units`); it grades nothing itself. All copy comes from utils/launchWording.js.
// Colours are App.css tokens only. Visual proof is Manual M5.
import React from 'react';
import { WORDING, UI_WORDING, fill, captureGapHint } from '../../utils/launchWording';
import { unitGapReason, NO_BRANCH_UNIT, OBSERVE_UNIT } from '../../utils/launchRollup';
import { maskIdText } from '../../utils/evidenceHar';

const MAX_VALUES = 5;

// Token triplets per status. A status with no entry renders plain text, not a badge.
const TONE = {
  not_covered: ['var(--warn-bg)', 'var(--warn-text)', 'var(--warn-border)'],
  waived: ['var(--info-bg)', 'var(--info-text)', 'var(--info-border)'],
  not_met: ['var(--error-bg)', 'var(--error-text)', 'var(--error-border)'],
  needs_review: ['var(--warn-bg)', 'var(--warn-text)', 'var(--warn-border)'],
  met: ['var(--success-bg)', 'var(--success-text)', 'var(--success-border)'],
};

const badgeStyle = (status) => {
  const [bg, ink, line] = TONE[status] || ['var(--surface)', 'var(--muted)', 'var(--line)'];
  return { background: bg, color: ink, border: `1px solid ${line}`, borderRadius: 4, padding: '0 6px', fontSize: 12, fontWeight: 600 };
};

/**
 * observedLinesFor(unit, observed, resultsByCheckId) -> string[]. What was read, from the proxy's
 * observeCriterion() output (observe units) or the checks' persisted assert reads (everything else), in the
 * report's own words (WORDING.observed*). Capped at MAX_VALUES lines. Never throws; [] when nothing is held.
 */
export function observedLinesFor(unit, observed, resultsByCheckId) {
  const out = [];
  try {
    if (unit && unit.id === OBSERVE_UNIT) {
      for (const it of (observed && Array.isArray(observed.items) ? observed.items : [])) {
        const param = String(it.label || it.param || '');
        if (!it.read) {
          if (it.unreadable > 0) out.push(fill(WORDING.observedUnreadable, { param }));
          continue;
        }
        for (const v of (Array.isArray(it.values) ? it.values : [])) {
          if (out.length >= MAX_VALUES) return out;
          if (it.kind === 'presence' && v.value === 'absent') out.push(fill(WORDING.observedAbsent, { param }));
          else out.push(fill(WORDING.observedValue, { param, value: v.empty ? '(empty)' : String(v.value) }));
        }
      }
      return out;
    }
    const seen = new Set();
    for (const id of (unit && Array.isArray(unit.checkIds) ? unit.checkIds : [])) {
      const res = resultsByCheckId && (typeof resultsByCheckId.get === 'function' ? resultsByCheckId.get(id) : resultsByCheckId[id]);
      for (const f of (res && Array.isArray(res.findings) ? res.findings : [])) {
        for (const v of (f && Array.isArray(f.values) ? f.values : [])) {
          if (!v || v.role !== 'assert') continue;
          const param = String(v.param || v.source || '');
          const line = v.state === 'read' ? fill(WORDING.observedValue, { param, value: v.empty ? '(empty)' : String(v.value) })
            : v.state === 'absent' ? fill(WORDING.observedAbsent, { param })
              : fill(WORDING.observedUnreadable, { param });
          if (!seen.has(line) && seen.size < MAX_VALUES) { seen.add(line); out.push(line); }
        }
      }
    }
  } catch { /* observed values are additive */ }
  return out;
}

/**
 * gapHintsFor(unit, resultsByCheckId) -> string[]. The capture-gap copy for a Not covered unit (E18: USB /
 * logcat gaps say what USB capture cannot read and the alternatives). Falls back to the generic re-capture
 * line when the reason is unknown. Never throws.
 */
export function gapHintsFor(unit, resultsByCheckId) {
  try {
    const unread = Array.isArray(unit.unread) ? unit.unread : [];
    if (unread.length) return unread.map((item) => fill(WORDING.hints.observe_unread, { item }));
    const code = unit.naReason || unitGapReason(unit, resultsByCheckId) || (unit.reason === 'no_rows_matched' ? 'no_rows_matched' : null);
    const hint = code ? captureGapHint(code, {}) : null;
    return [hint || WORDING.notCoveredRecapture];
  } catch {
    return [WORDING.notCoveredRecapture];
  }
}

const lastFor = (list, criterionId, unitId) => {
  const l = Array.isArray(list) ? list : [];
  for (let i = l.length - 1; i >= 0; i -= 1) {
    if (l[i] && l[i].criterionId === criterionId && l[i].branchId === unitId) return l[i];
  }
  return null;
};

/**
 * <UnitRows units mode /> - one row per unit. `mode` is the rollup answer's `mode`; 'off' (or no units)
 * renders nothing. Optional: criterionId, labels {unitId: text}, observed (observeCriterion output),
 * resultsByCheckId, waivers[], voided[], noBranchField, onWaive(unitId) (offered on Not covered units only).
 */
export default function UnitRows({ units, mode, criterionId, labels, observed, resultsByCheckId, waivers, voided, noBranchField, onWaive }) {
  const list = Array.isArray(units) ? units : [];
  if (!list.length || (mode !== 'on' && mode !== 'shadow')) return null;
  const anyGap = list.some((u) => u && u.status === 'not_covered');
  return (
    <div data-testid="unit-rows" style={{ marginTop: 8 }}>
      {anyGap && (
        <div style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 4 }}>{WORDING.notCoveredDefinition}</div>
      )}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {list.filter(Boolean).map((u) => {
          const label = (labels && labels[u.id]) || u.label || u.id;
          const status = WORDING.statusLabels[u.status] || u.status;
          const waiver = u.status === 'waived' ? lastFor(waivers, criterionId, u.id) : null;
          const voidedBy = u.status === 'not_covered' ? lastFor(voided, criterionId, u.id) : null;
          const lines = observedLinesFor(u, observed, resultsByCheckId);
          const graded = u.status === 'met' && typeof u.graded === 'number';
          return (
            <li key={u.id} data-unit={u.id} style={{ padding: '4px 0', borderLeft: '2px solid var(--line)', paddingLeft: 8, marginBottom: 4 }}>
              {u.id === NO_BRANCH_UNIT ? (
                <span>{fill(WORDING.noBranchApplies, noBranchField ? { field: noBranchField } : {})}</span>
              ) : graded ? (
                <span data-testid="unit-line">{fill(WORDING.branchLineGraded, { label, status, graded: u.graded, unreadable: Number(u.unreadable) || 0 })}</span>
              ) : (
                <>
                  <span data-testid="unit-line">{fill(WORDING.branchLine, { label, status: '' }).trimEnd()}</span>{' '}
                  <span role="status" style={badgeStyle(u.status)}>{status}</span>
                </>
              )}
              {u.unconfirmed === true && (
                <span data-testid="unit-unconfirmed" style={{ marginLeft: 6, color: 'var(--muted)', fontSize: 12 }}>
                  ({WORDING.unconfirmedAnnotation})
                </span>
              )}
              {u.status === 'not_covered' && onWaive && (
                <button type="button" onClick={() => onWaive(u.id)} style={{ marginLeft: 8 }}>
                  {UI_WORDING.waiveAction}
                </button>
              )}
              {waiver && (
                <div data-testid="unit-waiver" style={{ fontSize: 12, color: 'var(--muted)' }}>
                  {fill(WORDING.waivedLine, { signer: waiver.by, time: waiver.at, reason: maskIdText(String(waiver.reason == null ? '' : waiver.reason)) })}
                </div>
              )}
              {voidedBy && (
                <div data-testid="unit-voided" style={{ fontSize: 12, color: 'var(--muted)' }}>
                  {voidedBy.voided === 'build_changed' ? WORDING.waivedVoidBuild : WORDING.waivedVoidUnit}
                </div>
              )}
              {lines.map((l) => (
                <div key={l} data-testid="unit-observed" style={{ fontSize: 12, color: 'var(--ink)' }}>{l}</div>
              ))}
              {u.status === 'not_covered' && gapHintsFor(u, resultsByCheckId).map((h) => (
                <div key={h} data-testid="unit-hint" style={{ fontSize: 12, color: 'var(--muted)' }}>{h}</div>
              ))}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
